import { useCallback, useEffect, useMemo, useRef, useState, type MutableRefObject } from 'react';
import { sessionConfigQuery, sessionConfigSave } from '@/services/assets/api';
import { normalizeReelModel, useChatReelStore } from '@/stores/chat-reel';
import { useChatMuseStore, type MuseModelList, type MuseQuickTab } from '@/stores/chat-muse';
import { getModelList } from '@/services/muse/api';
import { useChatReelActions } from '@/hooks/chatReel';
import { applyReelModelList } from '@/pages/GenerationMedia/ai-chat-reel/components/chatBlock/modelSelectionStore';

type HistorySessionType = 'reel' | 'muse' | 'seo';

type HistorySessionModelListOptions = {
  type: HistorySessionType;
  sessionId: string | null;
  museType?: string | null;
  queryHistory?: boolean;
  onHistoryConfig?: (config: any) => void;
};

type ReelHistorySessionOptions = {
  sessionId: string | null;
};

type ImageHistorySessionOptions = {
  sessionId: string | null;
  museType: string | null;
};

type SeoSessionConfigPayload = {
  sessionId: string;
  url?: string;
  step?: number;
};

type SeoHistorySessionOptions = {
  validateSave?: (payload: SeoSessionConfigPayload) => boolean;
};

type RestoreSeoHistorySessionOptions = {
  isActive?: () => boolean;
};

type SeoHistoryConfig = {
  url?: string;
  step?: number;
};

const normalizeMuseModelList = (data?: Partial<MuseModelList> | null): MuseModelList => ({
  auto: Array.isArray(data?.auto) ? data.auto : [],
  image: Array.isArray(data?.image) ? data.image : [],
  reel: Array.isArray(data?.reel) ? data.reel : [],
});

// 保存历史会话配置失败不阻断当前创作流程，只在控制台记录排查信息。
export const saveHistorySessionConfig = async (data: any) => {
  try {
    await sessionConfigSave(data);
  } catch (error) {
    console.error('保存会话配置失败', error);
  }
};

// 历史会话查询会回填 store，调用方需要避免把这类回填误判为用户手动修改。
export const HistorySessionQuery = async (data: any, options?: { signal?: AbortSignal }) => {
  const setEntityCount = useChatReelStore.getState().setEntityCount;
  const setStoreSelectionDraft = useChatReelStore.getState().setStoreSelectionDraft;
  const setSkillObj = useChatReelStore.getState().setSkillObj;
  const setReelModel = useChatReelStore.getState().setReelModel;
  const setCanvasId = useChatReelStore.getState().setCanvasId;
  const clearHistoryConfig = () => {
    if (data.type === 'reel') {
      if (data.restoreGenerationParams !== false) {
        setStoreSelectionDraft(null);
      }
      setCanvasId(null);
    }
    if (data.type === 'muse') {
      const { setMuseSelectedModel, setMuseSelectedSize, setMuseSelectedResolution, setMuseActiveQuickTab, setSelectBrand } = useChatMuseStore.getState();
      setMuseSelectedModel(undefined);
      setMuseSelectedSize(undefined);
      setMuseSelectedResolution(undefined);
      setMuseActiveQuickTab([]);
      setSelectBrand({ id: '', brand_name: '' });
    }
  };
  try {
    const res = await sessionConfigQuery({ sessionId: data.sessionId }, options);
    if (data.type === 'reel' && useChatReelStore.getState().seessionId !== data.sessionId) return;
    if (data.type === 'muse' && useChatMuseStore.getState().seessionId !== data.sessionId) return;
    if (res.code === 200 && res.data) {
      if (data.type === 'reel') {
        if (data.restoreGenerationParams !== false && res.data?.generationParams) {
          setStoreSelectionDraft(res.data?.generationParams ?? {});
        }
        if (res.data?.skillConfig && res.data?.skillConfig.length > 0) {
          setSkillObj(res.data?.skillConfig[0]);
        }
        setEntityCount(Number(res.data?.entityCount) || 0, 'entity');
        setReelModel(normalizeReelModel(res.data?.modelSelect));
        const restoredCanvasId = res.data?.canvasId;
        if (restoredCanvasId) {
          setCanvasId(String(restoredCanvasId));
        }
      }
      if (data.type === 'muse') {
        const { setMuseSelectedModel, setMuseSelectedSize, setMuseSelectedResolution, setMuseActiveQuickTab, setSelectBrand, setCanvasId, setEntityCount } = useChatMuseStore.getState();
        const generationParams = res.data?.generationParams;
        const quickTabs = Array.isArray(res.data?.modelSelect) ? res.data.modelSelect.filter((item: string): item is MuseQuickTab => item === 'optimize' || item === 'inspiration') : [];
        setMuseSelectedModel(generationParams?.selectedModel);
        setMuseSelectedSize(generationParams?.selectedSize);
        setMuseSelectedResolution(generationParams?.selectedResolution);
        setMuseActiveQuickTab(quickTabs);
        setEntityCount(Number(res.data?.entityCount) || 0, 'entity');
        if (res.data.brandConfig && res.data.brandConfig.brandId && res.data.brandConfig.brandName) {
          setSelectBrand({ id: res.data.brandConfig.brandId, brand_name: res.data.brandConfig.brandName });
        }
        const restoredCanvasId = res.data?.canvasId;
        if (restoredCanvasId) setCanvasId(String(restoredCanvasId));
      }
      if (data.type === 'seo') {
        return res.data;
      }
      return res.data;
    }
    if (data.type === 'reel' || data.type === 'muse') {
      clearHistoryConfig();
    }
  } catch (error) {
    if (options?.signal?.aborted) {
      return;
    }
    if ((data.type === 'reel' && useChatReelStore.getState().seessionId === data.sessionId) || (data.type === 'muse' && useChatMuseStore.getState().seessionId === data.sessionId)) {
      clearHistoryConfig();
    }
    console.error('查询会话配置失败', error);
  }
};

// Reel 与 Muse 共用模型接口，但请求参数、响应结构和 store 相互隔离。
export const HistorySessionGetModelList = async (data: HistorySessionModelListOptions) => {
  if (data.type === 'reel') {
    if (!data.sessionId) return false;

    const reelState = useChatReelStore.getState();
    if (reelState.seessionId !== data.sessionId) return false;
    useChatReelStore.setState({ modelListReady: false });
    const params: { media_type: string; session_id: string; quick_tab?: string } = {
      media_type: 'reel',
      session_id: data.sessionId,
    };
    params.quick_tab = reelState.reelModel;

    const res = await getModelList(params);
    if (res.code !== 200 || !res.data?.reel) return false;
    if (useChatReelStore.getState().seessionId !== data.sessionId || useChatReelStore.getState().reelModel !== params.quick_tab) return false;

    applyReelModelList(res.data.reel);
    if (data.queryHistory) {
      await HistorySessionQuery({ sessionId: data.sessionId, type: 'reel' });
      const currentState = useChatReelStore.getState();
      if (currentState.seessionId !== data.sessionId) return false;
      if (currentState.storeSelectionDraft === undefined) {
        currentState.setStoreSelectionDraft(null);
      }
      currentState.resetHistorySessionChanged();
    }
    useChatReelStore.setState({ modelListReady: true });
    return true;
  }

  if (!data.museType) return false;

  const res = await getModelList({ media_type: data.museType }, { timeout: 1000 * 30 });
  if (res.code !== 200 || !res.data) return false;
  if (data.sessionId && useChatMuseStore.getState().seessionId !== data.sessionId) return false;

  useChatMuseStore.getState().setModelList(normalizeMuseModelList(res.data));
  if (data.queryHistory && data.sessionId) {
    const config = await HistorySessionQuery({ sessionId: data.sessionId, type: 'muse' });
    if (useChatMuseStore.getState().seessionId !== data.sessionId) return false;
    data.onHistoryConfig?.(config);
    useChatMuseStore.getState().resetMuseHistorySessionChanged();
  }
  return true;
};

// 带防抖的历史会话保存 Hook，enabled 用来区分初始化恢复和用户手动变更。
export const HistorySessionSave = (data: any, delay = 300, enabled = true, saveGenerationRef?: MutableRefObject<number>, validateSaveRef?: MutableRefObject<((payload: SeoSessionConfigPayload) => boolean) | undefined>) => {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const queuedSignatureRef = useRef<string | null>(null);
  const saveQueueRef = useRef<Promise<void>>(Promise.resolve());
  const dataSignature = useMemo(() => JSON.stringify(data ?? null), [data]);

  useEffect(() => {
    const clearTimer = () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };

    if (!data?.sessionId) {
      clearTimer();
      return;
    }

    clearTimer();

    if (!enabled) {
      if (saveGenerationRef) {
        saveGenerationRef.current += 1;
      }
      return clearTimer;
    }
    if (queuedSignatureRef.current === dataSignature) return clearTimer;

    const generationAtSchedule = saveGenerationRef?.current ?? 0;
    timerRef.current = setTimeout(() => {
      const payload = data;
      queuedSignatureRef.current = dataSignature;
      saveQueueRef.current = saveQueueRef.current.then(async () => {
        if (saveGenerationRef && saveGenerationRef.current !== generationAtSchedule) {
          return;
        }
        const validateSave = validateSaveRef?.current;
        if (validateSave && !validateSave(payload)) {
          return;
        }
        await saveHistorySessionConfig(payload);
      });
      timerRef.current = null;
    }, delay);

    return clearTimer;
  }, [data, dataSignature, delay, enabled, saveGenerationRef, validateSaveRef]);
};

// SEO 无模型列表，仅编排历史配置恢复与命令式 session config 保存。
export const useSeoHistorySession = ({ validateSave }: SeoHistorySessionOptions = {}) => {
  const queryAbortRef = useRef<AbortController | null>(null);
  const querySeqRef = useRef(0);
  const validateSaveRef = useRef(validateSave);
  validateSaveRef.current = validateSave;

  const abortPendingQuery = useCallback(() => {
    queryAbortRef.current?.abort();
    queryAbortRef.current = null;
    querySeqRef.current += 1;
  }, []);

  const invalidateSaveQueue = useCallback(() => {
    abortPendingQuery();
  }, [abortPendingQuery]);

  const persistSeoSessionConfig = useCallback(async (payload: SeoSessionConfigPayload) => {
    if (!payload.sessionId) return;

    const validate = validateSaveRef.current;
    if (validate && !validate(payload)) {
      return;
    }

    await saveHistorySessionConfig(payload);
  }, []);

  const restoreSeoHistorySession = useCallback(async (currentSessionId: string, applyConfig: (config?: SeoHistoryConfig) => void | Promise<void>, options?: RestoreSeoHistorySessionOptions) => {
    queryAbortRef.current?.abort();
    const querySeq = ++querySeqRef.current;
    const controller = new AbortController();
    queryAbortRef.current = controller;

    let config: SeoHistoryConfig | undefined;
    try {
      config = await HistorySessionQuery({ sessionId: currentSessionId, type: 'seo' }, { signal: controller.signal });
    } catch (error) {
      if (controller.signal.aborted) {
        return undefined;
      }
      console.error('查询 SEO 会话配置失败', error);
      return undefined;
    }

    if (querySeq !== querySeqRef.current) {
      return config;
    }
    if (options?.isActive && !options.isActive()) {
      return config;
    }
    await applyConfig(config);
    if (querySeq !== querySeqRef.current) {
      return config;
    }
    if (options?.isActive && !options.isActive()) {
      return config;
    }
    return config;
  }, []);

  return {
    restoreSeoHistorySession,
    persistSeoSessionConfig,
    invalidateSaveQueue,
  };
};

// Reel 页面历史会话编排：初始化先加载模型列表，再延迟恢复历史配置；之后仅响应用户变更保存。
export const useReelHistorySession = ({ sessionId }: ReelHistorySessionOptions) => {
  const [historySessionReady, setHistorySessionReady] = useState(false);
  const modelListRequestRef = useRef<{ sessionId: string; reelModel: string; queryHistory: boolean; request: Promise<void> } | null>(null);
  const { setCanvasId, setStoreSelectionDraft, resetHistorySessionChanged } = useChatReelActions();
  const storeSelectionDraft = useChatReelStore((state) => state.storeSelectionDraft);
  const reelModel = useChatReelStore((state) => state.reelModel);
  const skillObj = useChatReelStore((state) => state.skillObj);
  const agentStep = useChatReelStore((state) => state.agentStep);
  const canvasId = useChatReelStore((state) => state.canvasId);
  const entityCount = useChatReelStore((state) => state.entityCount);
  const historySessionChanged = useChatReelStore((state) => state.historySessionChanged);
  const skillId = skillObj?.id;
  const reelModelText = reelModel;
  const prevReelModelTextRef = useRef(reelModelText);

  // 自动保存只在初始化恢复完成后，并且用户手动改过参数时才打开。
  const sessionConfigSaveData = useMemo(() => {
    if (!sessionId) {
      return null;
    }
    return {
      sessionId,
      step: agentStep,
      modelSelect: [reelModel],
      skillIds: skillId ? [skillId] : [],
      generationParams: storeSelectionDraft || {},
      canvasId: canvasId || '',
      entityCount,
    };
  }, [agentStep, canvasId, entityCount, storeSelectionDraft, reelModel, sessionId, skillId]);

  HistorySessionSave(sessionConfigSaveData, 300, historySessionReady && historySessionChanged);

  const fetchModelList = useCallback((options?: { queryHistory?: boolean }): Promise<void> => {
    const { seessionId: currentSessionId, reelModel: currentReelModel } = useChatReelStore.getState();
    if (!currentSessionId) {
      return Promise.resolve();
    }
    if (modelListRequestRef.current?.sessionId === currentSessionId && modelListRequestRef.current.reelModel === currentReelModel) {
      if (options?.queryHistory && !modelListRequestRef.current.queryHistory) {
        return modelListRequestRef.current.request.then(() => fetchModelList(options));
      }
      return modelListRequestRef.current.request;
    }

    const request = (async () => {
      try {
        await HistorySessionGetModelList({
          type: 'reel',
          sessionId: currentSessionId,
          queryHistory: options?.queryHistory,
        });
      } catch (error) {
        console.error('初始化 Reel 历史会话失败', error);
      } finally {
        if (options?.queryHistory && useChatReelStore.getState().seessionId === currentSessionId) {
          setHistorySessionReady(true);
        }
      }
    })().finally(() => {
      if (modelListRequestRef.current?.request === request) {
        modelListRequestRef.current = null;
      }
    });
    modelListRequestRef.current = { sessionId: currentSessionId, reelModel: currentReelModel, queryHistory: Boolean(options?.queryHistory), request };
    return request;
  }, []);

  useEffect(() => {
    if (sessionId) {
      setHistorySessionReady(false);
      setCanvasId(null);
      setStoreSelectionDraft(undefined);
      resetHistorySessionChanged();
      fetchModelList({ queryHistory: true });
    }
  }, [fetchModelList, resetHistorySessionChanged, sessionId, setCanvasId, setStoreSelectionDraft]);

  useEffect(() => {
    if (!historySessionReady) {
      prevReelModelTextRef.current = reelModelText;
      return;
    }
    if (prevReelModelTextRef.current === reelModelText) {
      return;
    }
    prevReelModelTextRef.current = reelModelText;
    fetchModelList();
  }, [fetchModelList, historySessionReady, reelModelText]);

  return { fetchModelList, historySessionReady };
};

// Image 与 Reel 保持相同的历史会话编排：模型列表完成后恢复 session config，再开放画布恢复和配置保存。
export const useImageHistorySession = ({ sessionId, museType }: ImageHistorySessionOptions) => {
  const [historySessionReady, setHistorySessionReady] = useState(false);
  const [canvasRestoreId, setCanvasRestoreId] = useState<string | null>(null);
  const modelListRequestRef = useRef<{ requestKey: string; request: Promise<void> } | null>(null);
  const readyRequestKeyRef = useRef('');
  const museSelectedModel = useChatMuseStore((state) => state.museSelectedModel);
  const museSelectedSize = useChatMuseStore((state) => state.museSelectedSize);
  const museSelectedResolution = useChatMuseStore((state) => state.museSelectedResolution);
  const museActiveQuickTab = useChatMuseStore((state) => state.museActiveQuickTab);
  const selectBrand = useChatMuseStore((state) => state.selectBrand);
  const canvasId = useChatMuseStore((state) => state.canvasId);
  const entityCount = useChatMuseStore((state) => state.entityCount);
  const historySessionChanged = useChatMuseStore((state) => state.museHistorySessionChanged);

  const sessionConfigSaveData = useMemo(() => {
    if (!sessionId) return null;

    return {
      sessionId,
      canvasId: canvasId || undefined,
      brandId: selectBrand?.id || undefined,
      modelSelect: museActiveQuickTab.filter((item) => item === 'optimize' || item === 'inspiration'),
      generationParams: {
        selectedModel: museSelectedModel,
        selectedSize: museSelectedSize,
        selectedResolution: museSelectedResolution,
      },
      entityCount,
    };
  }, [canvasId, entityCount, museActiveQuickTab, museSelectedModel, museSelectedSize, museSelectedResolution, selectBrand, sessionId]);

  HistorySessionSave(sessionConfigSaveData, 300, historySessionReady && historySessionChanged);

  const fetchModelList = useCallback((): Promise<void> => {
    const currentState = useChatMuseStore.getState();
    const currentSessionId = currentState.seessionId;
    const currentMuseType = currentState.museType;
    if (!currentSessionId || !currentMuseType) return Promise.resolve();

    const requestKey = `${currentMuseType}:${currentSessionId}`;
    if (modelListRequestRef.current?.requestKey === requestKey) {
      return modelListRequestRef.current.request;
    }

    const request = (async () => {
      let restoredCanvasId: string | null = null;
      const loaded = await HistorySessionGetModelList({
        type: 'muse',
        sessionId: currentSessionId,
        museType: currentMuseType,
        queryHistory: true,
        onHistoryConfig: (config) => {
          const configCanvasId = config?.canvasId || config?.canvas_id;
          restoredCanvasId = configCanvasId ? String(configCanvasId) : null;
        },
      });
      const latestState = useChatMuseStore.getState();
      if (loaded && latestState.seessionId === currentSessionId && latestState.museType === currentMuseType) {
        readyRequestKeyRef.current = requestKey;
        setCanvasRestoreId(restoredCanvasId);
        setHistorySessionReady(true);
      }
    })().finally(() => {
      if (modelListRequestRef.current?.request === request) {
        modelListRequestRef.current = null;
      }
    });

    modelListRequestRef.current = { requestKey, request };
    return request;
  }, []);

  useEffect(() => {
    readyRequestKeyRef.current = '';
    setHistorySessionReady(false);
    setCanvasRestoreId(null);
    useChatMuseStore.getState().resetMuseHistorySessionChanged();
    if (sessionId && museType) fetchModelList();
  }, [fetchModelList, museType, sessionId]);

  return {
    historySessionReady: historySessionReady && readyRequestKeyRef.current === `${museType}:${sessionId}`,
    canvasRestoreId,
  };
};

export type HistoryAgentNameHeader = 'seo' | 'insight' | 'muse';
/** 重命名/删除会话时 X-Agent-Name：seo / insight；image、reel、muse 统一 muse */
export function resolveAgentNameHeader(item: { agent_family?: string; category?: string }): HistoryAgentNameHeader {
  const family = item.agent_family?.toLowerCase().trim() || '';
  const category = String(item.category || '')
    .toLowerCase()
    .trim();

  if (family === 'seo' || family === 'seo blog' || category === 'seo' || category === 'seo blog') {
    return 'seo';
  }
  if (family === 'insight' || category === 'insight') {
    return 'insight';
  }
  return 'muse';
}
